import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { getCurrentUser } from '../../../webmodule-common/other/api/current-user';
import { LitElementBase } from '../../../webmodule-common/other/litelement-base';
import { TenantLoginPublicInfo } from '../../api/supplier-api-interface-user';
import { tlang } from '../../../webmodule-common/other/language/lang';
import {
  WebmoduleCombobox,
  WebmoduleComboboxOption
} from '../../../webmodule-common/components/src/webmodule-components';

@customElement('webmodule-assignto')
export class WebModuleAssignUserEdit extends LitElementBase {
  @property() users?: TenantLoginPublicInfo[];
  @property() value?: string;

  eventPrefix() {
    return 'wm-event';
  }

  protected render(): unknown {
    const users = this.users ?? [];
    const userId = getCurrentUser()?.id ?? emptyGuid;
    const me = users.find(x => x.id === userId);
    const assignedUser = users.find(x => x.id === this.value);
    const isAssignedUserInactive = !!assignedUser && !assignedUser.isActive;

    const activeUsers = users
      .filter(x => x.id !== userId && x.isActive)
      .sort((a, b) => (a.friendlyName ?? '').localeCompare(b.friendlyName ?? ''));

    // Ordering matches the previous control: Unassigned, then the current user
    // ("me"), then the remaining active users alphabetically.
    const options: WebmoduleComboboxOption[] = [{ value: emptyGuid, text: tlang`Unassigned` }];
    if (me) {
      options.push({ value: me.id, text: me.friendlyName ?? '', disabled: !me.isActive });
    }
    options.push(...activeUsers.map(x => ({ value: x.id, text: x.friendlyName ?? '' })));

    // If the assigned user is inactive, show them at the end (disabled) so the
    // current assignment is still visible without being re-selectable.
    if (isAssignedUserInactive && assignedUser) {
      options.push({ value: assignedUser.id, text: assignedUser.friendlyName ?? '', disabled: true });
    }

    const disabled = this.attributeTrue('readonly') || this.attributeTrue('disabled');

    const changeEvent = (e: Event) => {
      this.value = (e.target as WebmoduleCombobox).value || emptyGuid;
      this.dispatchCustom('changed', { value: this.value });
    };

    return html`
      <webmodule-combobox
        size="small"
        hoist
        placeholder=${tlang`Unassigned`}
        .options=${options}
        .value=${this.value ?? emptyGuid}
        ?disabled=${disabled}
        @webmodule-change=${changeEvent}
      ></webmodule-combobox>
    `;
  }
}
